export const LEADING_INDICATOR_WATCH_SCHEMA_VERSION = 'leading_indicator_watch_v1' as const;
export const LEADING_INDICATOR_WATCH_EVENT_SCHEMA_VERSION =
  'leading_indicator_watch_event_v1' as const;

export type LeadingIndicatorWatchSchemaVersion = typeof LEADING_INDICATOR_WATCH_SCHEMA_VERSION;
export type LeadingIndicatorWatchEventSchemaVersion =
  typeof LEADING_INDICATOR_WATCH_EVENT_SCHEMA_VERSION;

export type WatchRegimeTag = {
  label: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
};

export type WatchSeriesIdentity =
  | {
      kind: 'governed_metric';
      metricId: string;
      metricLabel?: string | null;
      scopeKind?: string | null;
      scopeId?: string | null;
      unit?: string | null;
      sourceRef?: string | null;
    }
  | {
      kind: 'market_series';
      provider: 'fred' | 'eia' | 'bls' | 'market_loader' | 'other';
      seriesId: string;
      sourceSeriesId: string;
      label: string;
      unit: string;
      frequency: string;
      regimeTag?: WatchRegimeTag | null;
      sourceRef?: string | null;
    };

export type WatchThresholdDirection = 'above' | 'below';
export type WatchTrendDirection = 'up' | 'down';
export type WatchConditionDirection = WatchThresholdDirection | WatchTrendDirection;

export type WatchCondition =
  | {
      kind: 'threshold_cross';
      direction: WatchThresholdDirection;
      threshold: number;
      unit?: string | null;
    }
  | {
      kind: 'trend_break';
      direction: WatchTrendDirection;
      baselineWindowSize: number;
      evaluationWindowSize: number;
      minimumChangePct: number;
    }
  | {
      kind: 'regime_relative_deviation';
      direction: WatchThresholdDirection;
      baselineWindowSize: number;
      minimumDeviationPct: number;
      requiredRegimeTagLabel?: string | null;
    };

export type WatchAffectedIdentity = {
  kind: 'commitment' | 'metric' | 'segment';
  id: string;
  label: string;
  scopeKind?: string | null;
  scopeId?: string | null;
};

export type WatchCadence = {
  interval: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  timezone?: string | null;
};

export type WatchProvenance = {
  sourceClass: 'authored' | 'loaded_fixture' | 'market_loader' | 'governed_metric';
  authoredBy?: string | null;
  authoredAt?: string | null;
  loadedFrom?: string | null;
  evidenceRefs: readonly string[];
  notes?: string | null;
};

export type LeadingIndicatorWatch = {
  schemaVersion: LeadingIndicatorWatchSchemaVersion;
  id: string;
  label: string;
  series: WatchSeriesIdentity;
  condition: WatchCondition;
  affectedIdentity: WatchAffectedIdentity;
  cadence: WatchCadence;
  provenance: WatchProvenance;
  suggestedQuestion: string;
};

export type WatchSeriesPoint = {
  asOf: string;
  value: number;
  unit?: string | null;
  regimeTag?: WatchRegimeTag | null;
  sourceRef?: string | null;
};

export type WatchSeriesWindow = {
  series: WatchSeriesIdentity;
  points: readonly WatchSeriesPoint[];
};

export type WatchWindowSpan = {
  startAsOf: string;
  endAsOf: string;
  size: number;
};

export type WatchEventCrossed =
  | {
      kind: 'threshold_cross';
      direction: WatchThresholdDirection;
      value: number;
      previousValue: number;
      comparisonValue: number;
      delta: number;
      magnitude: number;
    }
  | {
      kind: 'trend_break';
      direction: WatchTrendDirection;
      value: number;
      comparisonValue: number;
      delta: number;
      deltaPct: number;
      magnitude: number;
      baselineWindow: WatchWindowSpan;
      evaluationWindow: WatchWindowSpan;
    }
  | {
      kind: 'regime_relative_deviation';
      direction: WatchThresholdDirection;
      value: number;
      comparisonValue: number;
      delta: number;
      deltaPct: number;
      magnitude: number;
      baselineWindow: WatchWindowSpan;
      regimeTag: WatchRegimeTag;
    };

export type WatchEvent = {
  schemaVersion: LeadingIndicatorWatchEventSchemaVersion;
  id: string;
  watchId: string;
  watchLabel: string;
  series: WatchSeriesIdentity;
  affectedIdentity: WatchAffectedIdentity;
  cadence: WatchCadence;
  provenance: WatchProvenance;
  asOf: string;
  sourceRef?: string | null;
  crossed: WatchEventCrossed;
  suggestedQuestion: string;
  regimeContext?: WatchRegimeTag | null;
};

export type WatchCannotEvaluateCode =
  | 'series_mismatch'
  | 'series_unavailable'
  | 'invalid_condition'
  | 'invalid_series_window'
  | 'insufficient_history'
  | 'missing_regime_tag'
  | 'regime_mismatch'
  | 'unsupported_condition_for_series';

export type WatchCannotEvaluateReason = {
  code: WatchCannotEvaluateCode;
  message: string;
  requiredPoints?: number;
  availablePoints?: number;
  requiredRegimeTagLabel?: string | null;
};

export type WatchEvaluationResult =
  | {
      status: 'fired';
      watchId: string;
      asOf: string;
      event: WatchEvent;
    }
  | {
      status: 'not_fired';
      watchId: string;
      asOf: string;
      observedValue: number;
    }
  | {
      status: 'cannot_evaluate';
      watchId: string;
      asOf: string | null;
      reason: WatchCannotEvaluateReason;
    };

export function evaluateLeadingIndicatorWatch(
  watch: LeadingIndicatorWatch,
  seriesWindow: WatchSeriesWindow
): WatchEvaluationResult {
  const points = normalizeSeriesPoints(seriesWindow.points);
  const latest = points[points.length - 1] ?? null;
  const invalidCondition = validateWatchCondition(watch.condition);
  if (invalidCondition !== null) {
    return cannotEvaluate(watch, latest, invalidCondition);
  }

  if (!seriesIdentitiesMatch(watch.series, seriesWindow.series)) {
    return cannotEvaluate(watch, latest, {
      code: 'series_mismatch',
      message: 'Watch series identity does not match the supplied series window.',
    });
  }

  if (points.length !== seriesWindow.points.length) {
    return cannotEvaluate(watch, latest, {
      code: 'invalid_series_window',
      message: 'Series window contains a non-finite value and cannot be evaluated.',
    });
  }

  switch (watch.condition.kind) {
    case 'threshold_cross':
      return evaluateThresholdCross(watch, points);
    case 'trend_break':
      return evaluateTrendBreak(watch, points);
    case 'regime_relative_deviation':
      return evaluateRegimeRelativeDeviation(watch, seriesWindow.series, points);
  }
}

function evaluateThresholdCross(
  watch: LeadingIndicatorWatch,
  points: readonly WatchSeriesPoint[]
): WatchEvaluationResult {
  const requiredPoints = 2;
  const latest = points[points.length - 1] ?? null;
  if (points.length < requiredPoints || latest === null) {
    return insufficientHistory(watch, latest, requiredPoints, points.length);
  }

  const previous = points[points.length - 2];
  const condition = watch.condition;
  if (condition.kind !== 'threshold_cross') {
    return cannotEvaluate(watch, latest, {
      code: 'unsupported_condition_for_series',
      message: 'Expected a threshold-cross condition.',
    });
  }

  const crossed =
    condition.direction === 'above'
      ? previous.value <= condition.threshold && latest.value > condition.threshold
      : previous.value >= condition.threshold && latest.value < condition.threshold;

  if (!crossed) return notFired(watch, latest);

  const delta = latest.value - condition.threshold;
  return fired(
    watch,
    latest,
    {
      kind: 'threshold_cross',
      direction: condition.direction,
      value: latest.value,
      previousValue: previous.value,
      comparisonValue: condition.threshold,
      delta,
      magnitude: Math.abs(delta),
    },
    latest.regimeTag ?? regimeFromSeries(watch.series)
  );
}

function evaluateTrendBreak(
  watch: LeadingIndicatorWatch,
  points: readonly WatchSeriesPoint[]
): WatchEvaluationResult {
  const condition = watch.condition;
  const latest = points[points.length - 1] ?? null;
  if (condition.kind !== 'trend_break') {
    return cannotEvaluate(watch, latest, {
      code: 'unsupported_condition_for_series',
      message: 'Expected a trend-break condition.',
    });
  }

  const requiredPoints = condition.baselineWindowSize + condition.evaluationWindowSize;
  if (points.length < requiredPoints || latest === null) {
    return insufficientHistory(watch, latest, requiredPoints, points.length);
  }

  const baseline = points.slice(
    points.length - requiredPoints,
    points.length - condition.evaluationWindowSize
  );
  const evaluation = points.slice(points.length - condition.evaluationWindowSize);
  const baselineAverage = average(baseline);
  const evaluationAverage = average(evaluation);

  if (baselineAverage === 0) {
    return cannotEvaluate(watch, latest, {
      code: 'invalid_series_window',
      message: 'Trend-break baseline average is zero, so percentage change is undefined.',
    });
  }

  const delta = evaluationAverage - baselineAverage;
  const deltaPct = (delta / Math.abs(baselineAverage)) * 100;
  const crossed =
    condition.direction === 'up'
      ? deltaPct >= condition.minimumChangePct
      : deltaPct <= -condition.minimumChangePct;

  if (!crossed) return notFired(watch, latest);

  return fired(
    watch,
    latest,
    {
      kind: 'trend_break',
      direction: condition.direction,
      value: evaluationAverage,
      comparisonValue: baselineAverage,
      delta,
      deltaPct,
      magnitude: Math.abs(delta),
      baselineWindow: spanForWindow(baseline),
      evaluationWindow: spanForWindow(evaluation),
    },
    latest.regimeTag ?? regimeFromSeries(watch.series)
  );
}

function evaluateRegimeRelativeDeviation(
  watch: LeadingIndicatorWatch,
  series: WatchSeriesIdentity,
  points: readonly WatchSeriesPoint[]
): WatchEvaluationResult {
  const condition = watch.condition;
  const latest = points[points.length - 1] ?? null;
  if (condition.kind !== 'regime_relative_deviation') {
    return cannotEvaluate(watch, latest, {
      code: 'unsupported_condition_for_series',
      message: 'Expected a regime-relative-deviation condition.',
    });
  }

  if (series.kind !== 'market_series') {
    return cannotEvaluate(watch, latest, {
      code: 'unsupported_condition_for_series',
      message: 'Regime-relative deviation requires a market series.',
    });
  }

  if (latest === null) {
    return insufficientHistory(watch, latest, condition.baselineWindowSize + 1, 0);
  }

  const latestRegime = resolveRegimeTag(latest, series);
  if (latestRegime === null) {
    return cannotEvaluate(watch, latest, {
      code: 'missing_regime_tag',
      message: 'Market-series point has no regime tag for regime-relative comparison.',
    });
  }

  const requiredRegimeTagLabel = condition.requiredRegimeTagLabel ?? latestRegime.label;
  if (latestRegime.label !== requiredRegimeTagLabel) {
    return cannotEvaluate(watch, latest, {
      code: 'regime_mismatch',
      message: 'Latest point is not in the regime required by the watch condition.',
      requiredRegimeTagLabel,
    });
  }

  const sameRegimeHistory = points
    .slice(0, -1)
    .filter((point) => resolveRegimeTag(point, series)?.label === requiredRegimeTagLabel);
  if (sameRegimeHistory.length < condition.baselineWindowSize) {
    return cannotEvaluate(watch, latest, {
      code: 'insufficient_history',
      message: 'Series does not contain enough same-regime history for comparison.',
      requiredPoints: condition.baselineWindowSize + 1,
      availablePoints: sameRegimeHistory.length + 1,
      requiredRegimeTagLabel,
    });
  }

  const baseline = sameRegimeHistory.slice(-condition.baselineWindowSize);
  const baselineAverage = average(baseline);
  if (baselineAverage === 0) {
    return cannotEvaluate(watch, latest, {
      code: 'invalid_series_window',
      message: 'Regime baseline average is zero, so percentage deviation is undefined.',
    });
  }

  const delta = latest.value - baselineAverage;
  const deltaPct = (delta / Math.abs(baselineAverage)) * 100;
  const crossed =
    condition.direction === 'above'
      ? deltaPct >= condition.minimumDeviationPct
      : deltaPct <= -condition.minimumDeviationPct;

  if (!crossed) return notFired(watch, latest);

  return fired(
    watch,
    latest,
    {
      kind: 'regime_relative_deviation',
      direction: condition.direction,
      value: latest.value,
      comparisonValue: baselineAverage,
      delta,
      deltaPct,
      magnitude: Math.abs(delta),
      baselineWindow: spanForWindow(baseline),
      regimeTag: latestRegime,
    },
    latestRegime
  );
}

function normalizeSeriesPoints(points: readonly WatchSeriesPoint[]): WatchSeriesPoint[] {
  const finitePoints = points.filter((point) => Number.isFinite(point.value));
  return [...finitePoints].sort((a, b) => a.asOf.localeCompare(b.asOf));
}

function validateWatchCondition(condition: WatchCondition): WatchCannotEvaluateReason | null {
  switch (condition.kind) {
    case 'threshold_cross':
      if (!Number.isFinite(condition.threshold)) {
        return invalidCondition('Threshold-cross watches require a finite threshold.');
      }
      return null;
    case 'trend_break':
      if (
        !isPositiveSafeInteger(condition.baselineWindowSize) ||
        !isPositiveSafeInteger(condition.evaluationWindowSize) ||
        !isNonNegativeFinite(condition.minimumChangePct)
      ) {
        return invalidCondition(
          'Trend-break watches require positive integer windows and a finite non-negative percentage threshold.'
        );
      }
      return null;
    case 'regime_relative_deviation':
      if (
        !isPositiveSafeInteger(condition.baselineWindowSize) ||
        !isNonNegativeFinite(condition.minimumDeviationPct)
      ) {
        return invalidCondition(
          'Regime-relative deviation watches require a positive integer baseline window and a finite non-negative percentage threshold.'
        );
      }
      return null;
  }
}

function invalidCondition(message: string): WatchCannotEvaluateReason {
  return {
    code: 'invalid_condition',
    message,
  };
}

function isPositiveSafeInteger(value: number): boolean {
  return Number.isSafeInteger(value) && value > 0;
}

function isNonNegativeFinite(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

function seriesIdentitiesMatch(left: WatchSeriesIdentity, right: WatchSeriesIdentity): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === 'market_series' && right.kind === 'market_series') {
    return left.provider === right.provider && left.seriesId === right.seriesId;
  }
  if (left.kind === 'governed_metric' && right.kind === 'governed_metric') {
    return (
      left.metricId === right.metricId &&
      (left.scopeKind ?? null) === (right.scopeKind ?? null) &&
      (left.scopeId ?? null) === (right.scopeId ?? null)
    );
  }
  return false;
}

function average(points: readonly WatchSeriesPoint[]): number {
  return points.reduce((sum, point) => sum + point.value, 0) / points.length;
}

function spanForWindow(points: readonly WatchSeriesPoint[]): WatchWindowSpan {
  const first = points[0];
  const last = points[points.length - 1] ?? first;
  return {
    startAsOf: first.asOf,
    endAsOf: last.asOf,
    size: points.length,
  };
}

function fired(
  watch: LeadingIndicatorWatch,
  latest: WatchSeriesPoint,
  crossed: WatchEventCrossed,
  regimeContext?: WatchRegimeTag | null
): WatchEvaluationResult {
  return {
    status: 'fired',
    watchId: watch.id,
    asOf: latest.asOf,
    event: {
      schemaVersion: LEADING_INDICATOR_WATCH_EVENT_SCHEMA_VERSION,
      id: `${watch.id}:${latest.asOf}:${crossed.kind}`,
      watchId: watch.id,
      watchLabel: watch.label,
      series: watch.series,
      affectedIdentity: watch.affectedIdentity,
      cadence: watch.cadence,
      provenance: watch.provenance,
      asOf: latest.asOf,
      sourceRef: latest.sourceRef ?? sourceRefFromSeries(watch.series),
      crossed,
      suggestedQuestion: watch.suggestedQuestion,
      regimeContext,
    },
  };
}

function notFired(watch: LeadingIndicatorWatch, latest: WatchSeriesPoint): WatchEvaluationResult {
  return {
    status: 'not_fired',
    watchId: watch.id,
    asOf: latest.asOf,
    observedValue: latest.value,
  };
}

function insufficientHistory(
  watch: LeadingIndicatorWatch,
  latest: WatchSeriesPoint | null,
  requiredPoints: number,
  availablePoints: number
): WatchEvaluationResult {
  return cannotEvaluate(watch, latest, {
    code: 'insufficient_history',
    message: 'Series window does not contain enough history for this watch condition.',
    requiredPoints,
    availablePoints,
  });
}

function cannotEvaluate(
  watch: LeadingIndicatorWatch,
  latest: WatchSeriesPoint | null,
  reason: WatchCannotEvaluateReason
): WatchEvaluationResult {
  return {
    status: 'cannot_evaluate',
    watchId: watch.id,
    asOf: latest?.asOf ?? null,
    reason,
  };
}

function sourceRefFromSeries(series: WatchSeriesIdentity): string | null {
  if (series.kind === 'market_series') {
    return series.sourceRef ?? `${series.provider}:${series.sourceSeriesId}`;
  }
  return series.sourceRef ?? null;
}

function regimeFromSeries(series: WatchSeriesIdentity): WatchRegimeTag | null {
  return series.kind === 'market_series' ? (series.regimeTag ?? null) : null;
}

function resolveRegimeTag(
  point: WatchSeriesPoint,
  series: WatchSeriesIdentity
): WatchRegimeTag | null {
  return point.regimeTag ?? regimeFromSeries(series);
}
